import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { storage } from '../../data/localStorage';
import { cloService } from '../../services/cloService';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const { 
  FiCode, FiEye, FiSave, FiDownload, FiUpload, FiArrowLeft, FiPrinter, 
  FiRefreshCw, FiFileText, FiCpu, FiEdit3, FiBook, FiPlus, FiMinus, 
  FiTarget, FiList, FiX, FiChevronDown, FiChevronRight, FiCheckCircle,
  FiLayout, FiType, FiAlignCenter, FiAlignLeft, FiAlignRight 
} = FiIcons;

// Default HTML template for body page
const DEFAULT_BODY_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>LLE3012 Final Exam</title>
  <style>
    /* Reset */
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: Arial, sans-serif;
      font-size: 12pt;
      line-height: 1.15;
      color: black;
      background: white;
      counter-reset: page-number 3;
      /* Start at page 4 */
    }
    /* Print Settings */
    @page {
      size: A4;
      margin: 0;
    }
    @media print {
      body, html {
        margin: 0;
        padding: 0;
      }
      .page-break {
        page-break-before: always;
        break-before: page;
      }
    }
    /* Page Container with Exact Margins */
    .page-container {
      width: calc(8.27in - 1.18in - 1.18in);
      /* A4 width minus left/right */
      max-width: 100%;
      margin: 0 auto;
      padding: 0.58in 0;
      /* Top/bottom margin simulation */
    }
    .page-content {
      position: relative;
      width: 100%;
      min-height: 11.69in;
      padding-left: 0.50in;
      padding-right: 0.5in;
      box-sizing: border-box;
    }
    /* Header: (CONFIDENTIAL) right-aligned */
    .exam-header {
      text-align: right;
      font-size: 12pt;
      font-weight: bold;
      margin-bottom: 20px;
    }
    /* CLO Table Title */
    .clo-table-title {
      text-align: center;
      font-weight: bold;
      font-size: 12pt;
      margin: 20px 0 5px 0;
    }
    /* CLO Table */
    .clo-table {
      width: 100%;
      border-collapse: collapse;
      margin: 15px 0 20px 0;
      font-size: 11pt;
    }
    .clo-table td {
      border: 1px solid black;
      padding: 6px 8px;
      vertical-align: top;
    }
    .clo-label, .plo-label {
      font-weight: bold;
      width: 10%;
    }
    .description {
      font-weight: normal;
      width: 80%;
    }
    /* Section titles - LEFT ALIGNED */
    .section-title {
      font-size: 13pt;
      font-weight: bold;
      margin: 25px 0 15px 0;
      text-align: left;
    }
    .instruction {
      margin: 12px 0;
      font-weight: normal;
    }
    /* Section A: Lowercase a., b., etc. */
    .section-a-list {
      list-style-type: lower-alpha;
      margin-left: 20px;
      margin-top: 10px;
    }
    .section-a-list li {
      margin: 10px 0;
    }
    /* Sub-items: Lowercase Roman i., ii., etc. */
    .roman-list {
      list-style-type: lower-roman;
      margin-left: 30px;
      margin-top: 6px;
    }
    .roman-list li {
      margin: 6px 0;
    }
    /* Section B & C: Arabic 1., 2., etc. */
    .numbered-list {
      list-style-type: decimal;
      margin-left: 20px;
      margin-top: 10px;
    }
    .numbered-list li {
      margin: 10px 0;
    }
    /* Fill-in blanks */
    .fill-line {
      display: inline-block;
      min-width: 120px;
      border-bottom: 1px solid black;
      margin: 0 4px;
      vertical-align: bottom;
    }
    /* Multi-line response areas with lined paper effect */
    .response-area {
      width: 100%;
      min-height: 60px;
      background-image: repeating-linear-gradient(
        transparent 0px,
        transparent 18px,
        black 18px,
        black 18px
      );
      background-position: left bottom;
      background-size: 100% 18px;
      margin: 10px 0;
    }
    .synthesis-area {
      width: 100%;
      min-height: 120px;
      background-image: repeating-linear-gradient(
        transparent 0px,
        transparent 18px,
        black 18px,
        black 18px
      );
      background-position: left bottom;
      background-size: 100% 18px;
      margin: 10px 0;
    }
    .essay-area {
      width: 100%;
      min-height: 800px;
      background-image: repeating-linear-gradient(
        transparent 0px,
        transparent 18px,
        black 18px,
        black 18px
      );
      background-position: left top;
      background-size: 100% 18px;
      margin: 10px 0;
    }
    /* CLO/PLO Marker - Right-aligned and bold */
    .clo-marker {
      text-align: right;
      font-weight: bold;
      font-size: 10pt;
      margin: 15px 0 5px;
    }
    /* References */
    .references {
      margin-top: 15px;
      margin-left: 25px;
      text-indent: -25px;
      font-size: 10pt;
    }
    .references p {
      margin: 5px 0;
    }
    /* Footer */
    .footer {
      display: flex;
      justify-content: space-between;
      font-size: 10pt;
      width: 100%;
      margin-top: 30px;
    }
    .footer-left {
      text-align: left;
    }
    .footer-center::before {
      content: counter(page-number) " of 10";
    }
    .footer-right {
      text-align: right;
    }
    /* Page counter */
    .page-counter {
      counter-increment: page-number;
      height: 1px;
    }
  </style>
</head>
<body contenteditable="true">
  <!-- Page 1 -->
  <div class="page-container">
    <div class="page-content">
      <div class="exam-header">(CONFIDENTIAL)</div>
      <div class="clo-table-title">COURSE LEARNING OUTCOMES</div>
      <table class="clo-table">
        <tr>
          <td class="clo-label">CLO1</td>
          <td class="description">Produce detailed text with accurate techniques of paraphrasing (C3).</td>
          <td class="plo-label">PLO1</td>
        </tr>
        <tr>
          <td class="clo-label">CLO2</td>
          <td class="description">Synthesise information and arguments from a number of sources (C2).</td>
          <td class="plo-label">PLO1</td>
        </tr>
        <tr>
          <td class="clo-label">CLO3</td>
          <td class="description">Produce synthesised essays which develops an argument, giving reasons in support of or against a particular point of view (C3).</td>
          <td class="plo-label">PLO2</td>
        </tr>
      </table>
      <div class="section-title">SECTION A</div>
      <div class="instruction">ANSWER ALL QUESTIONS</div>
      <div class="instruction">USING SYNONYMS (10 MARKS)</div>
      <ol class="section-a-list">
        <li>Select the best synonym for the underlined word.
          <ol class="roman-list">
            <li>The <u>outcome</u> of the research was surprising.
              <ul style="list-style-type: lower-alpha; margin-left: 30px; margin-top: 6px;">
                <li>a) method</li>
                <li>b) result</li>
                <li>c) purpose</li>
                <li>d) hypothesis</li>
              </ul>
              <div class="clo-marker">(CLO1:PLO1 - 2 marks)</div>
            </li>
            <li>The professor <u>recommended</u> additional readings.
              <ul style="list-style-type: lower-alpha; margin-left: 30px; margin-top: 6px;">
                <li>a) assigned</li>
                <li>b) suggested</li>
                <li>c) ordered</li>
                <li>d) noted</li>
              </ul>
              <div class="clo-marker">(CLO1:PLO1 - 2 marks)</div>
            </li>
            <li>She had a <u>significant</u> impact on the study.
              <ul style="list-style-type: lower-alpha; margin-left: 30px; margin-top: 6px;">
                <li>a) minor</li>
                <li>b) casual</li>
                <li>c) substantial</li>
                <li>d) negligible</li>
              </ul>
              <div class="clo-marker">(CLO1:PLO1 - 2 marks)</div>
            </li>
            <li>The findings were <u>revealed</u> in the latest journal.
              <ul style="list-style-type: lower-alpha; margin-left: 30px; margin-top: 6px;">
                <li>a) kept</li>
                <li>b) edited</li>
                <li>c) published</li>
                <li>d) erased</li>
              </ul>
              <div class="clo-marker">(CLO1:PLO1 - 2 marks)</div>
            </li>
            <li>They must <u>complete</u> the assignment by Friday.
              <ul style="list-style-type: lower-alpha; margin-left: 30px; margin-top: 6px;">
                <li>a) fulfil</li>
                <li>b) finish</li>
                <li>c) discuss</li>
                <li>d) submit</li>
              </ul>
              <div class="clo-marker">(CLO1:PLO1 - 2 marks)</div>
            </li>
          </ol>
        </li>
      </ol>
      <div class="instruction">PASSIVE-ACTIVE VOICE (10 MARKS)</div>
      <div class="instruction">Instructions: Rewrite the following sentences by changing the voice from passive to active or vice versa, as directed.</div>
      <ol class="numbered-list">
        <li>The report was written by the student. (Convert to active voice)
          <div class="response-area"></div>
          <div class="clo-marker">(CLO1:PLO1 - 2 marks)</div>
        </li>
        <li>Researchers conducted the survey last month. (Convert to passive voice)
          <div class="response-area"></div>
          <div class="clo-marker">(CLO1:PLO1 - 2 marks)</div>
        </li>
        <li>The data was analysed by the professor. (Convert to active voice)
          <div class="response-area"></div>
          <div class="clo-marker">(CLO1:PLO1 - 2 marks)</div>
        </li>
        <li>The committee will announce the results tomorrow. (Convert to passive voice)
          <div class="response-area"></div>
          <div class="clo-marker">(CLO1:PLO1 - 2 marks)</div>
        </li>
        <li>The essay was reviewed by the peer group. (Convert to active voice)
          <div class="response-area"></div>
          <div class="clo-marker">(CLO1:PLO1 - 2 marks)</div>
        </li>
      </ol>
      <div class="instruction">LINKERS (10 MARKS)</div>
      <div class="instruction">Instructions: Complete the following sentences using the most appropriate linkers to show comparison or contrast. Choose from the given options: for example, however, in contrast, as a result, moreover.</div>
      <ol class="numbered-list">
        <li>The new policy was highly effective in reducing waste; <span class="fill-line"></span>, recycling rates increased by 25% within six months.
          <div class="clo-marker">(CLO1:PLO1 - 2 marks)</div>
        </li>
        <li>Online education offers various benefits; <span class="fill-line"></span>, students can access lectures at any time and from any location.
          <div class="clo-marker">(CLO1:PLO1 - 2 marks)</div>
        </li>
        <li>The research highlights the challenges faced by students during remote learning; <span class="fill-line"></span>, it provides solutions for better engagement.
          <div class="clo-marker">(CLO1:PLO1 - 2 marks)</div>
        </li>
        <li>The new approach improved efficiency; <span class="fill-line"></span>, the traditional method was time-consuming.
          <div class="clo-marker">(CLO1:PLO1 - 2 marks)</div>
        </li>
        <li>This research highlights positive aspects; <span class="fill-line"></span>, the literature review indicates potential drawbacks.
          <div class="clo-marker">(CLO1:PLO1 - 2 marks)</div>
        </li>
      </ol>
      <!-- Footer Page 1 -->
      <div class="footer">
        <div class="footer-left">Final Exam . Sem . II , Session 2024 / 2025 (S1)</div>
        <div class="footer-center"></div>
        <div class="footer-right">LLE 3012</div>
      </div>
      <div class="page-counter"></div>
    </div>
  </div>
  <!-- Page 2 -->
  <div class="page-container">
    <div class="page-content page-break">
      <div class="section-title">SECTION B</div>
      <div class="instruction">ANSWER ALL QUESTIONS</div>
      <div class="instruction">PARAPHRASING SKILLS (20 MARKS)</div>
      <div class="instruction">Instructions: Paraphrase the following paragraphs using a combination of techniques such as synonyms, passive-active voice changes, and sentence restructuring. Cite the text correctly using APA style in-text citations.</div>
      <ol class="numbered-list">
        <li>"Educational technology has revolutionized the learning environment, providing more interactive and student-centred approaches. This transformation allows students to engage actively in the learning process, enhancing their critical thinking and problem-solving skills" (Anderson & Johnson, 2018).
          <div class="response-area"></div>
          <div class="clo-marker">(CLO1:PLO1 - 10 marks)</div>
        </li>
        <li>"The increase in urban population has led to greater environmental challenges, including pollution and waste management issues. Cities are struggling to maintain sustainable practices to accommodate the growing number of residents" (John Smith, 2020).
          <div class="response-area"></div>
          <div class="clo-marker">(CLO1:PLO1 - 10 marks)</div>
        </li>
      </ol>
      <div class="instruction">SYNTHESISING SKILLS (20 MARKS)</div>
      <div class="instruction">Instructions: Read the following two excerpts and write a short paragraph (120–150 words) synthesising the ideas. Use appropriate linkers and APA style in-text citations.</div>
      <p><strong>Text 1:</strong> "Many researchers argue that remote work increases productivity by reducing commuting time and providing a flexible environment. However, some studies suggest that it may also blur the boundaries between work and personal life, leading to burnout" (Alan Brown, 2021).</p>
      <p><strong>Text 2:</strong> "Although remote work has been shown to enhance productivity in some sectors, it may not be suitable for all industries. The lack of face-to-face interaction can hinder team collaboration and communication, particularly in creative fields" (Samuel Green, 2022).</p>
      <div class="synthesis-area"></div>
      <div class="clo-marker">(CLO2:PLO1 - 20 marks)</div>
      <!-- Footer Page 2 -->
      <div class="footer">
        <div class="footer-left">Final Exam . Sem . II , Session 2024 / 2025 (S1)</div>
        <div class="footer-center"></div>
        <div class="footer-right">LLE 3012</div>
      </div>
      <div class="page-counter"></div>
    </div>
  </div>
  <!-- Page 3 -->
  <div class="page-container">
    <div class="page-content page-break">
      <div class="section-title">SECTION C</div>
      <div class="instruction">ANSWER ALL QUESTIONS</div>
      <div class="instruction">ESSAY WRITING (30 MARKS)</div>
      <div class="instruction">Instructions: Write a five-paragraph synthesis essay (Introduction, three Body Paragraphs, and Conclusion) on the given topic. Your informative essay should utilise paraphrasing, in-text citations, appropriate sequence connectors and linkers by synthesising the provided sources.</div>
      <div class="instruction"><strong>Requirements:</strong></div>
      <div class="instruction">Topic: The Impact of Technology on Modern Education: Benefits and Drawbacks</div>
      <div class="instruction">Introduction: Include a clear thesis statement that presents your argument.</div>
      <div class="instruction">Body Paragraphs: Each paragraph should focus on a different aspect (e.g., benefits of technology, drawbacks, and impact on learning outcomes).</div>
      <div class="instruction">Conclusion: Summarize the key points and restate your position.</div>
      <div class="instruction">References: Please state the references used for the in-text citations.</div>
      <div class="instruction">Use the following sources to support your ideas:</div>
      <div class="references">
        <p>"While online learning may challenge the development of critical thinking skills, it has significantly improved access to resources that were previously unavailable to many. Students can now explore digital libraries, research databases, and educational platforms that were once limited to specific institutions. A study revealed that 80% of participants agreed online learning gave them opportunities to access specialized knowledge, which helped broaden their understanding of various topics." – Anisah Karim and Johan Ridzuan</p>
        <p><strong>Document type:</strong> Journal article<br>
          <strong>Authors:</strong> Anisah Karim and Johan Ridzuan<br>
          <strong>Publication year:</strong> 2022<br>
          <strong>Article title:</strong> Online Learning as a Bridge to Knowledge Accessibility<br>
          <strong>Journal information:</strong> Advances in Digital Education<br>
          <strong>Volume 7 • Issue 4 • Pages 15–28</strong><br>
          <strong>DOI:</strong> https://doi.org/10.2190/ade202200004</p>
        <p>"The level of critical thinking among school children was found to be significantly affected by their heavy reliance on technology. They struggle to solve problems using analytical skills and often turn to the internet for answers. Seventy percent of the respondents surveyed admitted that they used the World Wide Web to help with their homework." – Zuraina Burhan and Mohd Farhan</p>
        <p><strong>Document type:</strong> Journal article<br>
          <strong>Authors:</strong> Zuraina Burhan and Mohd Farhan<br>
          <strong>Publication year:</strong> 2021<br>
          <strong>Article title:</strong> The Impact of Technology on Children's Critical Thinking Skills<br>
          <strong>Journal information:</strong> Technology, Online Learning and Education<br>
          <strong>Volume 5 • Issue 3 • Pages 56–68</strong><br>
          <strong>DOI:</strong> https://doi.org/10.1037/tyr0000182</p>
        <p>"Online learning often encourages students to memorize information instead of thinking critically. Many students rely on recorded lessons and automated quizzes, which give them fewer chances to discuss ideas or think deeply. A recent study showed that 55% of students preferred using online tools for quick answers rather than exploring topics in detail, which made it harder for them to solve problems on their own." – Nurul Huda and Aiman Hakim</p>
        <p><strong>Document type:</strong> Journal article<br>
          <strong>Authors:</strong> Nurul Huda and Aiman Hakim<br>
          <strong>Publication year:</strong> 2022<br>
          <strong>Article title:</strong> Online Learning and Its Effects on Critical Thinking Development<br>
          <strong>Journal information:</strong> Education and Digital Innovation<br>
          <strong>Volume 8 • Issue 1 • Pages 25–38</strong><br>
          <strong>DOI:</strong> https://doi.org/10.1080/edi2022000034</p>
      </div>
      <div class="essay-area"></div>
      <div class="clo-marker">(CLO3:PLO2 - 30 marks)</div>
      <!-- Footer Page 3 -->
      <div class="footer">
        <div class="footer-left">Final Exam . Sem . II , Session 2024 / 2025 (S1)</div>
        <div class="footer-center"></div>
        <div class="footer-right">LLE 3012</div>
      </div>
      <div class="page-counter"></div>
    </div>
  </div>
</body>
</html>`;

// Default CSS for the template
const DEFAULT_BODY_CSS = ``;

const HTMLCSSEditor = ({ onBack, template = null }) => {
  const { currentWorkspace } = useWorkspace();
  
  const [templateSettings, setTemplateSettings] = useState({
    name: template?.name || 'Untitled HTML Template',
    pageSize: 'A4',
    orientation: 'portrait'
  });

  const [templateType, setTemplateType] = useState('body');
  const [showTemplateForm, setShowTemplateForm] = useState(true);
  const [showFormatPanel, setShowFormatPanel] = useState(false);

  // Available questions and CLOs from database
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [availableCLOs, setAvailableCLOs] = useState([]);
  const [showCLOSelector, setShowCLOSelector] = useState(false);
  const [selectedCLOIndex, setSelectedCLOIndex] = useState(null);

  // Cover page form data
  const [coverFormData, setCoverFormData] = useState({
    exam_type: 'FINAL EXAMINATION',
    programme_level: 'DEGREE PROGRAMME',
    semester_session: 'SEMESTER II ACADEMIC SESSION 2024/2025',
    course_title: 'ENGLISH FOR ACADEMIC WRITING',
    course_code: 'LLE 3012',
    duration: '2 HOURS',
    programme_code: 'ALL ACADEMIC PROGRAMMES',
    total_pages_text: 'TWELVE (12)',
    instructions: [
      'ANSWER ALL QUESTIONS IN SECTION A, B AND C.',
      'ANSWER ALL QUESTIONS IN THE QUESTION PAGE.'
    ]
  });

  // Enhanced body page form data
  const [bodyFormData, setBodyFormData] = useState({
    course_code: 'LLE3012',
    course_title: 'ENGLISH FOR ACADEMIC WRITING',
    semester_session: 'SEMESTER II, SESSION 2024/2025',
    clos: [
      {
        id: 'CLO1',
        number: 'CLO1',
        description: 'Produce detailed text with accurate techniques of paraphrasing (C3).',
        plo: 'PLO1'
      },
      {
        id: 'CLO2',
        number: 'CLO2',
        description: 'Synthesise information and arguments from a number of sources (C2).',
        plo: 'PLO1'
      },
      {
        id: 'CLO3',
        number: 'CLO3',
        description: 'Produce synthesised essays which develops an argument, giving reasons in support of or against a particular point of view (C3).',
        plo: 'PLO2'
      }
    ],
    sections: [
      {
        id: 'A',
        name: 'SECTION A',
        type: 'Multiple Choice',
        instruction: 'ANSWER ALL QUESTIONS',
        subsections: [
          {
            id: 'A1',
            title: 'USING SYNONYMS',
            marks: 10,
            questionCount: 5,
            questionType: 'Multiple Choice',
            clo: 'CLO1',
            plo: 'PLO1',
            marksPerQuestion: 2,
            questions: []
          },
          {
            id: 'A2',
            title: 'PASSIVE-ACTIVE VOICE',
            marks: 10,
            questionCount: 5,
            questionType: 'Short Answer',
            clo: 'CLO1',
            plo: 'PLO1',
            marksPerQuestion: 2,
            questions: []
          },
          {
            id: 'A3',
            title: 'LINKERS',
            marks: 10,
            questionCount: 5,
            questionType: 'Fill in the Blank',
            clo: 'CLO1',
            plo: 'PLO1',
            marksPerQuestion: 2,
            questions: []
          }
        ]
      },
      {
        id: 'B',
        name: 'SECTION B',
        type: 'Short Answer',
        instruction: 'ANSWER ALL QUESTIONS',
        subsections: [
          {
            id: 'B1',
            title: 'PARAPHRASING SKILLS',
            marks: 20,
            questionCount: 2,
            questionType: 'Essay',
            clo: 'CLO1',
            plo: 'PLO1',
            marksPerQuestion: 10,
            questions: []
          },
          {
            id: 'B2',
            title: 'SYNTHESISING SKILLS',
            marks: 20,
            questionCount: 1,
            questionType: 'Essay',
            clo: 'CLO2',
            plo: 'PLO1',
            marksPerQuestion: 20,
            questions: []
          }
        ]
      },
      {
        id: 'C',
        name: 'SECTION C',
        type: 'Essay',
        instruction: 'ANSWER ALL QUESTIONS',
        subsections: [
          {
            id: 'C1',
            title: 'ESSAY WRITING',
            marks: 30,
            questionCount: 1,
            questionType: 'Essay',
            clo: 'CLO3',
            plo: 'PLO2',
            marksPerQuestion: 30,
            questions: []
          }
        ]
      }
    ],
    // Format Settings
    format: {
      headerText: 'CONFIDENTIAL',
      headerAlign: 'right',
      pageNumbering: {
        start: 2,
        format: '{page} of {total}'
      },
      numbering: {
        section: 'alphabet', // alphabet, roman, decimal
        question: 'decimal',
        subQuestion: 'roman'
      },
      responseAreas: {
        shortAnswer: {
          lines: 3,
          lineHeight: 18
        },
        essay: {
          minHeight: 600,
          lineHeight: 18
        }
      },
      pageBreaks: ['sections'],
      footer: {
        left: 'Final Exam . Sem . II , Session 2024 / 2025 (S1)',
        center: '{page} of {total}',
        right: 'LLE 3012'
      }
    }
  });

  // Template content state
  const [htmlContent, setHtmlContent] = useState(template?.htmlContent || DEFAULT_BODY_HTML);
  const [cssContent, setCssContent] = useState(template?.cssContent || DEFAULT_BODY_CSS);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('form');
  const [isGenerating, setIsGenerating] = useState(false);
  const [formatExpanded, setFormatExpanded] = useState({
    header: false,
    pageNumbering: false,
    footer: false,
    responseAreas: false,
    numbering: false,
    pageBreaks: false
  });

  // Load questions and CLOs on component mount
  useEffect(() => {
    loadQuestions();
    loadCLOs();

    // Set default HTML if it's a new template
    if (!template?.htmlContent && templateType === 'body') {
      setHtmlContent(DEFAULT_BODY_HTML);
    }

    // Initialize format expansion state
    if (template?.formData?.format) {
      setBodyFormData(prev => ({
        ...prev,
        format: {
          ...prev.format,
          ...template.formData.format
        }
      }));
    }
  }, [currentWorkspace?.id]);

  const loadQuestions = () => {
    const questions = storage.getQuestions();
    setAvailableQuestions(questions);
  };

  const loadCLOs = async () => {
    try {
      let clos;
      if (currentWorkspace?.id) {
        clos = await cloService.getCLOs(currentWorkspace.id);
      } else {
        clos = storage.getCLOs();
      }
      setAvailableCLOs(clos);
    } catch (error) {
      console.error('Error loading CLOs:', error);
      const clos = storage.getCLOs();
      setAvailableCLOs(clos);
    }
  };

  // Auto-populate questions for each subsection
  const autoPopulateQuestions = (sectionIndex, subsectionIndex) => {
    const subsection = bodyFormData.sections[sectionIndex].subsections[subsectionIndex];
    const filteredQuestions = availableQuestions.filter(q =>
      q.type === subsection.questionType &&
      q.courseCode === bodyFormData.course_code
    );

    const selectedQuestions = filteredQuestions
      .sort(() => 0.5 - Math.random())
      .slice(0, subsection.questionCount);

    updateSubsection(sectionIndex, subsectionIndex, { questions: selectedQuestions });
  };

  // CLO Management Functions
  const openCLOSelector = (index) => {
    setSelectedCLOIndex(index);
    setShowCLOSelector(true);
  };

  const selectCLOFromDatabase = (selectedCLO) => {
    if (selectedCLOIndex !== null) {
      updateCLO(selectedCLOIndex, {
        id: selectedCLO.id,
        number: selectedCLO.cloNumber,
        description: selectedCLO.description,
        // Keep existing PLO or set default
        plo: bodyFormData.clos[selectedCLOIndex]?.plo || 'PLO1'
      });
    }
    setShowCLOSelector(false);
    setSelectedCLOIndex(null);
  };

  const addCLO = () => {
    const newCLO = {
      id: `CLO${bodyFormData.clos.length + 1}`,
      number: `CLO${bodyFormData.clos.length + 1}`,
      description: 'New Course Learning Outcome',
      plo: 'PLO1'
    };
    setBodyFormData(prev => ({
      ...prev,
      clos: [...prev.clos, newCLO]
    }));
  };

  const removeCLO = (index) => {
    setBodyFormData(prev => ({
      ...prev,
      clos: prev.clos.filter((_, i) => i !== index)
    }));
  };

  const updateCLO = (index, updates) => {
    setBodyFormData(prev => ({
      ...prev,
      clos: prev.clos.map((clo, i) => i === index ? { ...clo, ...updates } : clo)
    }));
  };

  // Section management functions
  const addSection = () => {
    const sectionId = String.fromCharCode(65 + bodyFormData.sections.length);
    const newSection = {
      id: sectionId,
      name: `SECTION ${sectionId}`,
      type: 'Multiple Choice',
      instruction: 'ANSWER ALL QUESTIONS',
      subsections: []
    };
    setBodyFormData(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
  };

  const removeSection = (index) => {
    setBodyFormData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const updateSection = (index, updates) => {
    setBodyFormData(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) => i === index ? { ...section, ...updates } : section)
    }));
  };

  // Subsection management functions
  const addSubsection = (sectionIndex) => {
    const section = bodyFormData.sections[sectionIndex];
    const newSubsection = {
      id: `${section.id}${section.subsections.length + 1}`,
      title: 'New Subsection',
      marks: 10,
      questionCount: 5,
      questionType: 'Multiple Choice',
      clo: 'CLO1',
      plo: 'PLO1',
      marksPerQuestion: 2,
      questions: []
    };
    updateSection(sectionIndex, {
      subsections: [...section.subsections, newSubsection]
    });
  };

  const removeSubsection = (sectionIndex, subsectionIndex) => {
    const section = bodyFormData.sections[sectionIndex];
    updateSection(sectionIndex, {
      subsections: section.subsections.filter((_, i) => i !== subsectionIndex)
    });
  };

  const updateSubsection = (sectionIndex, subsectionIndex, updates) => {
    const section = bodyFormData.sections[sectionIndex];
    const updatedSubsections = section.subsections.map((subsection, i) =>
      i === subsectionIndex ? { ...subsection, ...updates } : subsection
    );
    updateSection(sectionIndex, { subsections: updatedSubsections });
  };

  // Format settings functions
  const updateFormatSettings = (key, value) => {
    setBodyFormData(prev => ({
      ...prev,
      format: {
        ...prev.format,
        [key]: value
      }
    }));
  };

  const updateNestedFormatSetting = (parentKey, key, value) => {
    setBodyFormData(prev => ({
      ...prev,
      format: {
        ...prev.format,
        [parentKey]: {
          ...prev.format[parentKey],
          [key]: value
        }
      }
    }));
  };

  // Toggle format section expansion
  const toggleFormatSection = (section) => {
    setFormatExpanded(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Generate cover page template (keeping existing function)
  const generateCoverTemplate = () => {
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Exam Cover Page</title>
  <style>
    body {
      font-family: 'Arial', serif;
      font-size: 12pt;
      line-height: 2;
      margin: 1in;
      background: white;
    }
    
    .cover-box {
      border: 2px solid black;
      padding: 2em;
      max-width: 800px;
      margin: auto;
    }
    
    .logo-container {
      text-align: center;
      margin-bottom: 1em;
    }
    
    .logo {
      width: 300px;
      height: auto;
    }
    
    .center-text {
      text-align: center;
    }
    
    .left-text {
      text-align: left;
    }
    
    .institution p, .exam-title p, .footer-note {
      margin: 0.5em 0;
    }
    
    .course-info, .instructions {
      margin-top: 2em;
    }
    
    .divider-line, .note-line {
      border-top: 2px solid black;
      margin: 1em 0;
    }
    
    .indented {
      padding-left: 2pc;
    }
    
    .label {
      display: inline-block;
      width: 160px;
    }
    
    .value {
      display: inline-block;
    }
  </style>
</head>
<body>
  <div class="cover-box">
    <div class="logo-container">
      <img src="https://i.postimg.cc/ryrbYs4h/Logo-UPNM.png" alt="University Logo" class="logo">
    </div>
    <div class="institution center-text">
      <p><strong>NATIONAL DEFENCE UNIVERSITY OF MALAYSIA</strong></p>
      <p><strong>SUNGAI BESI CAMP</strong></p>
      <p><strong>57000 KUALA LUMPUR</strong></p>
      <div class="divider-line"></div>
    </div>
    <div class="exam-title center-text">
      <p><strong>${coverFormData.exam_type}</strong></p>
      <p><strong>${coverFormData.programme_level}</strong></p>
      <p><strong>${coverFormData.semester_session}</strong></p>
    </div>
    <div class="course-info left-text indented">
      <p><span class="label">COURSE</span> : <span class="value">${coverFormData.course_title}</span></p>
      <p><span class="label">COURSE CODE</span> : <span class="value">${coverFormData.course_code}</span></p>
      <p><span class="label">DURATION</span> : <span class="value">${coverFormData.duration}</span></p>
      <p><span class="label">PROGRAMME CODE</span> : <span class="value">${coverFormData.programme_code}</span></p>
    </div>
    <div class="instructions left-text indented">
      <p><strong>INSTRUCTIONS :</strong></p>
      ${coverFormData.instructions.map(instruction => `<p>${instruction}</p>`).join('\n      ')}
    </div>
    <div class="note-line"></div>
    <p class="footer-note center-text">
      THIS QUESTION PAPER CONTAINS ${coverFormData.total_pages_text} PAGES INCLUSIVE OF THIS PAGE.
    </p>
  </div>
</body>
</html>`;

    setHtmlContent(html);
    setCssContent('');
  };

  // Generate body page template based on form data
  const generateBodyTemplate = () => {
    // This function would generate HTML based on bodyFormData
    // For now, we're using the default template
    // In a real implementation, this would dynamically build the HTML
    setHtmlContent(DEFAULT_BODY_HTML);
    setCssContent('');
  };

  // Handle form submission
  const handleGenerateTemplate = () => {
    if (templateType === 'cover') {
      generateCoverTemplate();
    } else {
      generateBodyTemplate();
    }
    setShowTemplateForm(false);
    setActiveTab('html');
  };

  // File handling functions
  const handleFileUpload = (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      if (type === 'html') {
        setHtmlContent(content);
      } else if (type === 'css') {
        setCssContent(content);
      }
    };
    reader.readAsText(file);
  };

  const combineHTMLCSS = () => {
    if (!cssContent.trim()) return htmlContent;

    if (htmlContent.includes('<style>')) {
      return htmlContent.replace(
        /<style>[\s\S]*?<\/style>/,
        `<style>\n${cssContent}\n</style>`
      );
    } else {
      return htmlContent.replace(
        '</head>',
        `<style>\n${cssContent}\n</style>\n</head>`
      );
    }
  };

  // Update HTML with format settings
  const applyFormatSettings = (html) => {
    let updatedHTML = html;
    
    // Update header text
    updatedHTML = updatedHTML.replace(
      /<div class="exam-header">\([^<]*\)<\/div>/,
      `<div class="exam-header">(${bodyFormData.format.headerText})</div>`
    );
    
    // Update footer content
    updatedHTML = updatedHTML.replace(
      /<div class="footer-left">[^<]*<\/div>/g,
      `<div class="footer-left">${bodyFormData.format.footer.left}</div>`
    );
    
    updatedHTML = updatedHTML.replace(
      /<div class="footer-right">[^<]*<\/div>/g,
      `<div class="footer-right">${bodyFormData.format.footer.right}</div>`
    );
    
    // Update CSS for page numbering
    updatedHTML = updatedHTML.replace(
      /counter-reset: page-number \d+;/,
      `counter-reset: page-number ${bodyFormData.format.pageNumbering.start - 1};`
    );
    
    updatedHTML = updatedHTML.replace(
      /content: counter\(page-number\) " of \d+";/,
      `content: counter(page-number) " of ${bodyFormData.sections.length + 1}";`
    );
    
    return updatedHTML;
  };

  const generateTemplate = async () => {
    setIsGenerating(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Apply format settings to HTML
      const formattedHTML = applyFormatSettings(htmlContent);
      const finalHTML = combineHTMLCSS();
      
      const templateData = {
        id: template?.id || Date.now().toString(),
        name: templateSettings.name,
        type: 'html-css',
        templateType: templateType,
        formData: templateType === 'cover' ? coverFormData : bodyFormData,
        htmlContent: formattedHTML,
        cssContent,
        finalHTML,
        settings: templateSettings,
        createdAt: template?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const templates = JSON.parse(localStorage.getItem('exam_templates') || '[]');
      const existingIndex = templates.findIndex(t => t.id === templateData.id);

      if (existingIndex >= 0) {
        templates[existingIndex] = templateData;
      } else {
        templates.push(templateData);
      }

      localStorage.setItem('exam_templates', JSON.stringify(templates));
      alert('Template generated and saved successfully!');
    } catch (error) {
      console.error('Error generating template:', error);
      alert('Error generating template. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadHTML = () => {
    const finalHTML = combineHTMLCSS();
    const blob = new Blob([finalHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${templateSettings.name.replace(/\s+/g, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadPDF = () => {
    const finalHTML = combineHTMLCSS();
    const printWindow = window.open('', '_blank');
    printWindow.document.write(finalHTML);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Cover form management
  const addCoverInstruction = () => {
    setCoverFormData(prev => ({
      ...prev,
      instructions: [...prev.instructions, '']
    }));
  };

  const removeCoverInstruction = (index) => {
    setCoverFormData(prev => ({
      ...prev,
      instructions: prev.instructions.filter((_, i) => i !== index)
    }));
  };

  // Add question item to subsection
  const addQuestionItem = (sectionIndex, subsectionIndex) => {
    const subsection = bodyFormData.sections[sectionIndex].subsections[subsectionIndex];
    const newQuestion = {
      id: Date.now().toString(),
      text: 'New question item',
      type: subsection.questionType,
      options: subsection.questionType === 'Multiple Choice' ? ['Option A', 'Option B', 'Option C', 'Option D'] : [],
      marks: subsection.marksPerQuestion,
      clo: subsection.clo,
      plo: subsection.plo
    };
    
    const updatedQuestions = [...subsection.questions, newQuestion];
    updateSubsection(sectionIndex, subsectionIndex, { 
      questions: updatedQuestions,
      questionCount: updatedQuestions.length 
    });
  };

  // Update question item
  const updateQuestionItem = (sectionIndex, subsectionIndex, questionIndex, updates) => {
    const subsection = bodyFormData.sections[sectionIndex].subsections[subsectionIndex];
    const updatedQuestions = subsection.questions.map((q, i) => 
      i === questionIndex ? { ...q, ...updates } : q
    );
    updateSubsection(sectionIndex, subsectionIndex, { questions: updatedQuestions });
  };

  // Remove question item
  const removeQuestionItem = (sectionIndex, subsectionIndex, questionIndex) => {
    const subsection = bodyFormData.sections[sectionIndex].subsections[subsectionIndex];
    const updatedQuestions = subsection.questions.filter((_, i) => i !== questionIndex);
    updateSubsection(sectionIndex, subsectionIndex, { 
      questions: updatedQuestions,
      questionCount: updatedQuestions.length 
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="ml-72 min-h-screen flex flex-col">
        {/* Toolbar */}
        <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <motion.button
              onClick={onBack}
              className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiArrowLeft} />
              <span>Back to Templates</span>
            </motion.button>

            <div className="h-6 w-px bg-gray-300" />

            <SafeIcon icon={FiCode} className="text-blue-600" />
            <input
              type="text"
              value={templateSettings.name}
              onChange={(e) => setTemplateSettings(prev => ({ ...prev, name: e.target.value }))}
              className="text-lg font-semibold bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2"
            />

            {/* Template Type Selector */}
            <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => {
                  setTemplateType('cover');
                  setShowTemplateForm(true);
                  setActiveTab('form');
                }}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  templateType === 'cover'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <SafeIcon icon={FiFileText} className="inline mr-1" />
                Cover Page
              </button>
              <button
                onClick={() => {
                  setTemplateType('body');
                  setShowTemplateForm(true);
                  setActiveTab('form');
                }}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  templateType === 'body'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <SafeIcon icon={FiBook} className="inline mr-1" />
                Body Page
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowPreview(true)}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiEye} />
              <span>Preview</span>
            </button>
            <button
              onClick={downloadHTML}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiDownload} />
              <span>HTML</span>
            </button>
            <button
              onClick={downloadPDF}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiPrinter} />
              <span>PDF</span>
            </button>
            <motion.button
              onClick={generateTemplate}
              disabled={isGenerating || !htmlContent}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
              whileHover={{ scale: isGenerating ? 1 : 1.02 }}
              whileTap={{ scale: isGenerating ? 1 : 0.98 }}
            >
              {isGenerating ? (
                <>
                  <SafeIcon icon={FiRefreshCw} className="animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSave} />
                  <span>Save Template</span>
                </>
              )}
            </motion.button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex">
          {/* Left Panel - Form or Code Editor */}
          <div className="flex-1 flex flex-col">
            {/* Tabs */}
            <div className="bg-white border-b border-gray-200 flex">
              <button
                onClick={() => setActiveTab('form')}
                className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'form'
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <SafeIcon icon={FiEdit3} className="inline mr-2" />
                Template Form
              </button>
              <button
                onClick={() => setActiveTab('html')}
                className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'html'
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <SafeIcon icon={FiFileText} className="inline mr-2" />
                HTML
              </button>
              <button
                onClick={() => setActiveTab('css')}
                className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'css'
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <SafeIcon icon={FiCpu} className="inline mr-2" />
                CSS
              </button>
              <button
                onClick={() => setShowFormatPanel(!showFormatPanel)}
                className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                  showFormatPanel
                    ? 'border-purple-500 text-purple-600 bg-purple-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <SafeIcon icon={FiLayout} className="inline mr-2" />
                Format Settings
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto">
              {activeTab === 'form' && (
                <div className="p-6 space-y-6">
                  {templateType === 'cover' ? (
                    /* Cover Page Form */
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-800">Cover Page Template</h3>
                        <motion.button
                          onClick={handleGenerateTemplate}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          Generate Template
                        </motion.button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Exam Type
                          </label>
                          <select
                            value={coverFormData.exam_type}
                            onChange={(e) => setCoverFormData(prev => ({ ...prev, exam_type: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="FINAL EXAMINATION">FINAL EXAMINATION</option>
                            <option value="QUIZ">QUIZ</option>
                            <option value="MIDTERM TEST">MIDTERM TEST</option>
                            <option value="ASSIGNMENT">ASSIGNMENT</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Programme Level
                          </label>
                          <select
                            value={coverFormData.programme_level}
                            onChange={(e) => setCoverFormData(prev => ({ ...prev, programme_level: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="DEGREE PROGRAMME">DEGREE PROGRAMME</option>
                            <option value="DIPLOMA PROGRAMME">DIPLOMA PROGRAMME</option>
                            <option value="FOUNDATION PROGRAMME">FOUNDATION PROGRAMME</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Semester Session
                          </label>
                          <input
                            type="text"
                            value={coverFormData.semester_session}
                            onChange={(e) => setCoverFormData(prev => ({ ...prev, semester_session: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="SEMESTER II ACADEMIC SESSION 2024/2025"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Course Title
                          </label>
                          <input
                            type="text"
                            value={coverFormData.course_title}
                            onChange={(e) => setCoverFormData(prev => ({ ...prev, course_title: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="ENGLISH FOR ACADEMIC WRITING"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Course Code
                          </label>
                          <input
                            type="text"
                            value={coverFormData.course_code}
                            onChange={(e) => setCoverFormData(prev => ({ ...prev, course_code: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="LLE 3012"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Duration
                          </label>
                          <select
                            value={coverFormData.duration}
                            onChange={(e) => setCoverFormData(prev => ({ ...prev, duration: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="1 HOUR">1 HOUR</option>
                            <option value="1.5 HOURS">1.5 HOURS</option>
                            <option value="2 HOURS">2 HOURS</option>
                            <option value="2.5 HOURS">2.5 HOURS</option>
                            <option value="3 HOURS">3 HOURS</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Programme Code
                          </label>
                          <input
                            type="text"
                            value={coverFormData.programme_code}
                            onChange={(e) => setCoverFormData(prev => ({ ...prev, programme_code: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="ZB03, ZP53, ZC06, ALL ACADEMIC PROGRAMMES"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Total Pages Text
                          </label>
                          <input
                            type="text"
                            value={coverFormData.total_pages_text}
                            onChange={(e) => setCoverFormData(prev => ({ ...prev, total_pages_text: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="TWELVE (12)"
                          />
                        </div>
                      </div>

                      {/* Instructions */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Instructions
                        </label>
                        <div className="space-y-2">
                          {coverFormData.instructions.map((instruction, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={instruction}
                                onChange={(e) => {
                                  const newInstructions = [...coverFormData.instructions];
                                  newInstructions[index] = e.target.value;
                                  setCoverFormData(prev => ({ ...prev, instructions: newInstructions }));
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder={`Instruction ${index + 1}`}
                              />
                              <button
                                onClick={() => removeCoverInstruction(index)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={addCoverInstruction}
                            className="text-blue-600 hover:text-blue-700 text-sm"
                          >
                            + Add Instruction
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Enhanced Body Page Form */
                    <div className="space-y-8">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-800">Body Page Template</h3>
                        <motion.button
                          onClick={handleGenerateTemplate}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          Use Default Template
                        </motion.button>
                      </div>

                      {/* Basic Course Info */}
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h4 className="text-md font-semibold text-gray-800 mb-4">Course Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Course Code
                            </label>
                            <input
                              type="text"
                              value={bodyFormData.course_code}
                              onChange={(e) => setBodyFormData(prev => ({ ...prev, course_code: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="LLE3012"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Course Title
                            </label>
                            <input
                              type="text"
                              value={bodyFormData.course_title}
                              onChange={(e) => setBodyFormData(prev => ({ ...prev, course_title: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="ENGLISH FOR ACADEMIC WRITING"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Semester Session
                            </label>
                            <input
                              type="text"
                              value={bodyFormData.semester_session}
                              onChange={(e) => setBodyFormData(prev => ({ ...prev, semester_session: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="SEMESTER II, SESSION 2024/2025"
                            />
                          </div>
                        </div>
                      </div>

                      {/* CLO Management */}
                      <div className="bg-white p-6 rounded-lg border border-gray-200">
                        <h4 className="text-md font-semibold text-gray-800 mb-4">Course Learning Outcomes</h4>
                        
                        <div className="space-y-4">
                          {bodyFormData.clos.map((clo, index) => (
                            <div key={index} className="p-4 border border-gray-200 rounded-lg">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-2">
                                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm font-medium">
                                    {clo.number}
                                  </span>
                                  <span className="text-gray-500 text-sm">
                                    {clo.plo}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => openCLOSelector(index)}
                                    className="p-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-600 rounded"
                                  >
                                    Browse CLOs
                                  </button>
                                  <button
                                    onClick={() => removeCLO(index)}
                                    className="p-1 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>

                              <div className="space-y-2">
                                <textarea
                                  value={clo.description}
                                  onChange={(e) => updateCLO(index, { description: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                  placeholder="CLO description..."
                                  rows={2}
                                />
                                
                                <div className="flex items-center space-x-2">
                                  <label className="block text-xs font-medium text-gray-700">
                                    PLO:
                                  </label>
                                  <select
                                    value={clo.plo}
                                    onChange={(e) => updateCLO(index, { plo: e.target.value })}
                                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                                  >
                                    <option value="PLO1">PLO1</option>
                                    <option value="PLO2">PLO2</option>
                                    <option value="PLO3">PLO3</option>
                                    <option value="PLO4">PLO4</option>
                                    <option value="PLO5">PLO5</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          <button
                            onClick={addCLO}
                            className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-colors"
                          >
                            + Add Course Learning Outcome
                          </button>
                        </div>
                      </div>

                      {/* Sections & Questions */}
                      <div className="bg-white p-6 rounded-lg border border-gray-200">
                        <h4 className="text-md font-semibold text-gray-800 mb-4">Sections & Questions</h4>
                        
                        <div className="space-y-8">
                          {bodyFormData.sections.map((section, sectionIndex) => (
                            <div key={sectionIndex} className="p-4 border border-gray-200 rounded-lg">
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-2">
                                  <span className="bg-gray-800 text-white px-3 py-1 rounded-md text-sm font-medium">
                                    {section.name}
                                  </span>
                                </div>
                                <button
                                  onClick={() => removeSection(sectionIndex)}
                                  className="p-1 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded"
                                >
                                  Remove Section
                                </button>
                              </div>

                              <div className="space-y-3 mb-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Section Title
                                    </label>
                                    <input
                                      type="text"
                                      value={section.name}
                                      onChange={(e) => updateSection(sectionIndex, { name: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Section Type
                                    </label>
                                    <select
                                      value={section.type}
                                      onChange={(e) => updateSection(sectionIndex, { type: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                      <option value="Multiple Choice">Multiple Choice</option>
                                      <option value="Short Answer">Short Answer</option>
                                      <option value="Essay">Essay</option>
                                      <option value="Fill in the Blank">Fill in the Blank</option>
                                    </select>
                                  </div>
                                </div>
                                
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Section Instructions
                                  </label>
                                  <input
                                    type="text"
                                    value={section.instruction}
                                    onChange={(e) => updateSection(sectionIndex, { instruction: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="ANSWER ALL QUESTIONS"
                                  />
                                </div>
                              </div>

                              {/* Subsections */}
                              <div className="space-y-4 pl-4 border-l-2 border-gray-200">
                                {section.subsections.map((subsection, subsectionIndex) => (
                                  <div key={subsectionIndex} className="p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center justify-between mb-3">
                                      <div className="flex items-center space-x-2">
                                        <span className="bg-blue-50 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                          {subsection.title}
                                        </span>
                                        <span className="text-gray-500 text-xs">
                                          {subsection.marks} marks
                                        </span>
                                      </div>
                                      <button
                                        onClick={() => removeSubsection(sectionIndex, subsectionIndex)}
                                        className="p-1 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded"
                                      >
                                        Remove
                                      </button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                          Subsection Title
                                        </label>
                                        <input
                                          type="text"
                                          value={subsection.title}
                                          onChange={(e) => updateSubsection(sectionIndex, subsectionIndex, { title: e.target.value })}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                          Question Type
                                        </label>
                                        <select
                                          value={subsection.questionType}
                                          onChange={(e) => updateSubsection(sectionIndex, subsectionIndex, { questionType: e.target.value })}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        >
                                          <option value="Multiple Choice">Multiple Choice</option>
                                          <option value="Short Answer">Short Answer</option>
                                          <option value="Essay">Essay</option>
                                          <option value="Fill in the Blank">Fill in the Blank</option>
                                        </select>
                                      </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                          Total Marks
                                        </label>
                                        <input
                                          type="number"
                                          value={subsection.marks}
                                          onChange={(e) => updateSubsection(sectionIndex, subsectionIndex, { 
                                            marks: parseInt(e.target.value),
                                            marksPerQuestion: parseInt(e.target.value) / (subsection.questionCount || 1)
                                          })}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                          min="1"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                          CLO/PLO
                                        </label>
                                        <div className="flex items-center space-x-2">
                                          <select
                                            value={subsection.clo}
                                            onChange={(e) => updateSubsection(sectionIndex, subsectionIndex, { clo: e.target.value })}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                          >
                                            {bodyFormData.clos.map((clo) => (
                                              <option key={clo.id} value={clo.number}>
                                                {clo.number}
                                              </option>
                                            ))}
                                          </select>
                                          <span className="text-gray-500">:</span>
                                          <select
                                            value={subsection.plo}
                                            onChange={(e) => updateSubsection(sectionIndex, subsectionIndex, { plo: e.target.value })}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                          >
                                            <option value="PLO1">PLO1</option>
                                            <option value="PLO2">PLO2</option>
                                            <option value="PLO3">PLO3</option>
                                          </select>
                                        </div>
                                      </div>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                          Marks Per Question
                                        </label>
                                        <input
                                          type="number"
                                          value={subsection.marksPerQuestion}
                                          onChange={(e) => updateSubsection(sectionIndex, subsectionIndex, { marksPerQuestion: parseInt(e.target.value) })}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                          min="1"
                                        />
                                      </div>
                                    </div>

                                    {/* Question Items */}
                                    <div className="space-y-2 mt-4">
                                      <div className="flex items-center justify-between">
                                        <label className="block text-xs font-medium text-gray-700">
                                          Question Items ({subsection.questions.length})
                                        </label>
                                        <div className="flex items-center space-x-2">
                                          <button
                                            onClick={() => autoPopulateQuestions(sectionIndex, subsectionIndex)}
                                            className="px-2 py-1 text-xs bg-green-50 hover:bg-green-100 text-green-600 rounded"
                                          >
                                            Auto-populate
                                          </button>
                                          <button
                                            onClick={() => addQuestionItem(sectionIndex, subsectionIndex)}
                                            className="px-2 py-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-600 rounded"
                                          >
                                            Add Question
                                          </button>
                                        </div>
                                      </div>

                                      {subsection.questions.map((question, questionIndex) => (
                                        <div key={questionIndex} className="p-2 border border-gray-200 rounded bg-white text-sm">
                                          <div className="flex justify-between mb-1">
                                            <span className="font-medium">Q{questionIndex + 1}</span>
                                            <button
                                              onClick={() => removeQuestionItem(sectionIndex, subsectionIndex, questionIndex)}
                                              className="text-red-500 text-xs"
                                            >
                                              Remove
                                            </button>
                                          </div>
                                          <input
                                            type="text"
                                            value={question.text}
                                            onChange={(e) => updateQuestionItem(sectionIndex, subsectionIndex, questionIndex, { text: e.target.value })}
                                            className="w-full px-2 py-1 border border-gray-200 rounded text-sm mb-1"
                                            placeholder="Question text..."
                                          />
                                          
                                          {question.type === 'Multiple Choice' && (
                                            <div className="pl-3 space-y-1">
                                              {question.options?.map((option, optionIndex) => (
                                                <div key={optionIndex} className="flex items-center">
                                                  <span className="text-xs w-6">{String.fromCharCode(97 + optionIndex)})</span>
                                                  <input
                                                    type="text"
                                                    value={option}
                                                    onChange={(e) => {
                                                      const newOptions = [...question.options];
                                                      newOptions[optionIndex] = e.target.value;
                                                      updateQuestionItem(sectionIndex, subsectionIndex, questionIndex, { options: newOptions });
                                                    }}
                                                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs"
                                                  />
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}

                                <button
                                  onClick={() => addSubsection(sectionIndex)}
                                  className="w-full py-2 border border-dashed border-gray-300 rounded-lg text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-colors text-sm"
                                >
                                  + Add Subsection
                                </button>
                              </div>
                            </div>
                          ))}

                          <button
                            onClick={addSection}
                            className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-colors"
                          >
                            + Add New Section
                          </button>
                        </div>
                      </div>

                      <div className="bg-white p-6 rounded-lg border border-gray-200">
                        <p className="text-gray-600 mb-4">
                          This template follows the standard academic exam format with:
                        </p>
                        <ul className="list-disc pl-6 space-y-2 text-gray-600">
                          <li>Proper CLO and PLO references</li>
                          <li>Multiple choice, short answer, and essay question formats</li>
                          <li>Consistent header/footer with page numbering</li>
                          <li>Lined response areas for student answers</li>
                          <li>Proper section organization and question numbering</li>
                        </ul>
                        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center">
                            <SafeIcon icon={FiCheckCircle} className="text-blue-600 mr-2" />
                            <p className="text-blue-800 font-medium">The template HTML has been pre-loaded with the format you provided.</p>
                          </div>
                          <p className="mt-2 text-blue-700 text-sm">
                            You can click the "HTML" tab to view and edit the HTML directly, or click "Use Default Template" to reset to the original template.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'html' && (
                <div className="h-full">
                  <textarea
                    value={htmlContent}
                    onChange={(e) => setHtmlContent(e.target.value)}
                    className="w-full h-full p-4 font-mono text-sm border-none resize-none focus:outline-none"
                    placeholder="Generated HTML will appear here..."
                    spellCheck="false"
                    style={{ height: 'calc(100vh - 120px)' }}
                  />
                </div>
              )}

              {activeTab === 'css' && (
                <div className="h-full">
                  <textarea
                    value={cssContent}
                    onChange={(e) => setCssContent(e.target.value)}
                    className="w-full h-full p-4 font-mono text-sm border-none resize-none focus:outline-none"
                    placeholder="Add additional CSS here if needed..."
                    spellCheck="false"
                    style={{ height: 'calc(100vh - 120px)' }}
                  />
                </div>
              )}
              
              {showFormatPanel && (
                <div className="p-6 space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">Format Settings</h3>
                    <div className="text-sm text-gray-600">
                      Customize the appearance and behavior of the template
                    </div>
                  </div>
                  
                  {/* Header Settings */}
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <button 
                      className="flex items-center justify-between w-full"
                      onClick={() => toggleFormatSection('header')}
                    >
                      <div className="flex items-center">
                        <SafeIcon icon={FiType} className="text-blue-600 mr-2" />
                        <span className="font-medium">Header Settings</span>
                      </div>
                      <SafeIcon 
                        icon={formatExpanded.header ? FiChevronDown : FiChevronRight} 
                        className="text-gray-500" 
                      />
                    </button>
                    
                    {formatExpanded.header && (
                      <div className="mt-4 space-y-3 pl-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Header Text
                          </label>
                          <input
                            type="text"
                            value={bodyFormData.format.headerText}
                            onChange={(e) => updateFormatSettings('headerText', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="CONFIDENTIAL"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Header Alignment
                            </label>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => updateFormatSettings('headerAlign', 'left')}
                                className={`p-2 border border-gray-300 rounded ${
                                  bodyFormData.format.headerAlign === 'left' ? 'bg-blue-500 text-white' : 'bg-white'
                                }`}
                              >
                                <SafeIcon icon={FiAlignLeft} />
                              </button>
                              <button
                                onClick={() => updateFormatSettings('headerAlign', 'center')}
                                className={`p-2 border border-gray-300 rounded ${
                                  bodyFormData.format.headerAlign === 'center' ? 'bg-blue-500 text-white' : 'bg-white'
                                }`}
                              >
                                <SafeIcon icon={FiAlignCenter} />
                              </button>
                              <button
                                onClick={() => updateFormatSettings('headerAlign', 'right')}
                                className={`p-2 border border-gray-300 rounded ${
                                  (bodyFormData.format.headerAlign === 'right' || !bodyFormData.format.headerAlign) ? 'bg-blue-500 text-white' : 'bg-white'
                                }`}
                              >
                                <SafeIcon icon={FiAlignRight} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Page Numbering Settings */}
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <button 
                      className="flex items-center justify-between w-full"
                      onClick={() => toggleFormatSection('pageNumbering')}
                    >
                      <div className="flex items-center">
                        <SafeIcon icon={FiFileText} className="text-purple-600 mr-2" />
                        <span className="font-medium">Page Numbering</span>
                      </div>
                      <SafeIcon 
                        icon={formatExpanded.pageNumbering ? FiChevronDown : FiChevronRight} 
                        className="text-gray-500" 
                      />
                    </button>
                    
                    {formatExpanded.pageNumbering && (
                      <div className="mt-4 space-y-3 pl-6">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Start Page Number
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={bodyFormData.format.pageNumbering.start}
                              onChange={(e) => updateNestedFormatSetting('pageNumbering', 'start', parseInt(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Format
                            </label>
                            <input
                              type="text"
                              value={bodyFormData.format.pageNumbering.format}
                              onChange={(e) => updateNestedFormatSetting('pageNumbering', 'format', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="Page {page} of {total}"
                            />
                            <p className="text-xs text-gray-500 mt-1">Use {page} and {total} as placeholders</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Footer Settings */}
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <button 
                      className="flex items-center justify-between w-full"
                      onClick={() => toggleFormatSection('footer')}
                    >
                      <div className="flex items-center">
                        <SafeIcon icon={FiAlignLeft} className="text-green-600 mr-2" />
                        <span className="font-medium">Footer Settings</span>
                      </div>
                      <SafeIcon 
                        icon={formatExpanded.footer ? FiChevronDown : FiChevronRight} 
                        className="text-gray-500" 
                      />
                    </button>
                    
                    {formatExpanded.footer && (
                      <div className="mt-4 space-y-3 pl-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Left Footer
                          </label>
                          <input
                            type="text"
                            value={bodyFormData.format.footer.left}
                            onChange={(e) => updateNestedFormatSetting('footer', 'left', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="Final Exam . Sem . II , Session 2024 / 2025 (S1)"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Center Footer
                          </label>
                          <input
                            type="text"
                            value={bodyFormData.format.footer.center}
                            onChange={(e) => updateNestedFormatSetting('footer', 'center', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="{page} of {total}"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Right Footer
                          </label>
                          <input
                            type="text"
                            value={bodyFormData.format.footer.right}
                            onChange={(e) => updateNestedFormatSetting('footer', 'right', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="LLE 3012"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Response Area Settings */}
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <button 
                      className="flex items-center justify-between w-full"
                      onClick={() => toggleFormatSection('responseAreas')}
                    >
                      <div className="flex items-center">
                        <SafeIcon icon={FiAlignLeft} className="text-orange-600 mr-2" />
                        <span className="font-medium">Response Areas</span>
                      </div>
                      <SafeIcon 
                        icon={formatExpanded.responseAreas ? FiChevronDown : FiChevronRight} 
                        className="text-gray-500" 
                      />
                    </button>
                    
                    {formatExpanded.responseAreas && (
                      <div className="mt-4 space-y-3 pl-6">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Short Answer Lines
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={bodyFormData.format.responseAreas.shortAnswer.lines}
                              onChange={(e) => {
                                const lines = parseInt(e.target.value);
                                setBodyFormData(prev => ({
                                  ...prev,
                                  format: {
                                    ...prev.format,
                                    responseAreas: {
                                      ...prev.format.responseAreas,
                                      shortAnswer: {
                                        ...prev.format.responseAreas.shortAnswer,
                                        lines
                                      }
                                    }
                                  }
                                }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Line Height (px)
                            </label>
                            <input
                              type="number"
                              min="12"
                              max="36"
                              value={bodyFormData.format.responseAreas.shortAnswer.lineHeight}
                              onChange={(e) => {
                                const lineHeight = parseInt(e.target.value);
                                setBodyFormData(prev => ({
                                  ...prev,
                                  format: {
                                    ...prev.format,
                                    responseAreas: {
                                      ...prev.format.responseAreas,
                                      shortAnswer: {
                                        ...prev.format.responseAreas.shortAnswer,
                                        lineHeight
                                      }
                                    }
                                  }
                                }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Essay Min Height (px)
                            </label>
                            <input
                              type="number"
                              min="300"
                              step="50"
                              value={bodyFormData.format.responseAreas.essay.minHeight}
                              onChange={(e) => {
                                const minHeight = parseInt(e.target.value);
                                setBodyFormData(prev => ({
                                  ...prev,
                                  format: {
                                    ...prev.format,
                                    responseAreas: {
                                      ...prev.format.responseAreas,
                                      essay: {
                                        ...prev.format.responseAreas.essay,
                                        minHeight
                                      }
                                    }
                                  }
                                }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Essay Line Height (px)
                            </label>
                            <input
                              type="number"
                              min="12"
                              max="36"
                              value={bodyFormData.format.responseAreas.essay.lineHeight}
                              onChange={(e) => {
                                const lineHeight = parseInt(e.target.value);
                                setBodyFormData(prev => ({
                                  ...prev,
                                  format: {
                                    ...prev.format,
                                    responseAreas: {
                                      ...prev.format.responseAreas,
                                      essay: {
                                        ...prev.format.responseAreas.essay,
                                        lineHeight
                                      }
                                    }
                                  }
                                }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Numbering Style Settings */}
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <button 
                      className="flex items-center justify-between w-full"
                      onClick={() => toggleFormatSection('numbering')}
                    >
                      <div className="flex items-center">
                        <SafeIcon icon={FiList} className="text-indigo-600 mr-2" />
                        <span className="font-medium">Numbering Styles</span>
                      </div>
                      <SafeIcon 
                        icon={formatExpanded.numbering ? FiChevronDown : FiChevronRight} 
                        className="text-gray-500" 
                      />
                    </button>
                    
                    {formatExpanded.numbering && (
                      <div className="mt-4 space-y-3 pl-6">
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Section Numbering
                            </label>
                            <select
                              value={bodyFormData.format.numbering.section}
                              onChange={(e) => updateNestedFormatSetting('numbering', 'section', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="alphabet">Alphabet (A, B, C)</option>
                              <option value="roman">Roman (I, II, III)</option>
                              <option value="decimal">Decimal (1, 2, 3)</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Question Numbering
                            </label>
                            <select
                              value={bodyFormData.format.numbering.question}
                              onChange={(e) => updateNestedFormatSetting('numbering', 'question', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="decimal">Decimal (1, 2, 3)</option>
                              <option value="alphabet">Alphabet (a, b, c)</option>
                              <option value="roman">Roman (i, ii, iii)</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Sub-Question Numbering
                            </label>
                            <select
                              value={bodyFormData.format.numbering.subQuestion}
                              onChange={(e) => updateNestedFormatSetting('numbering', 'subQuestion', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="roman">Roman (i, ii, iii)</option>
                              <option value="alphabet">Alphabet (a, b, c)</option>
                              <option value="decimal">Decimal (1, 2, 3)</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Page Break Settings */}
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <button 
                      className="flex items-center justify-between w-full"
                      onClick={() => toggleFormatSection('pageBreaks')}
                    >
                      <div className="flex items-center">
                        <SafeIcon icon={FiFileText} className="text-red-600 mr-2" />
                        <span className="font-medium">Page Breaks</span>
                      </div>
                      <SafeIcon 
                        icon={formatExpanded.pageBreaks ? FiChevronDown : FiChevronRight} 
                        className="text-gray-500" 
                      />
                    </button>
                    
                    {formatExpanded.pageBreaks && (
                      <div className="mt-4 space-y-3 pl-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Insert page breaks at:
                          </label>
                          <div className="space-y-2">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={bodyFormData.format.pageBreaks.includes('sections')}
                                onChange={(e) => {
                                  const pageBreaks = [...bodyFormData.format.pageBreaks];
                                  if (e.target.checked) {
                                    if (!pageBreaks.includes('sections')) {
                                      pageBreaks.push('sections');
                                    }
                                  } else {
                                    const index = pageBreaks.indexOf('sections');
                                    if (index !== -1) {
                                      pageBreaks.splice(index, 1);
                                    }
                                  }
                                  updateFormatSettings('pageBreaks', pageBreaks);
                                }}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">New sections</span>
                            </label>
                            
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={bodyFormData.format.pageBreaks.includes('subsections')}
                                onChange={(e) => {
                                  const pageBreaks = [...bodyFormData.format.pageBreaks];
                                  if (e.target.checked) {
                                    if (!pageBreaks.includes('subsections')) {
                                      pageBreaks.push('subsections');
                                    }
                                  } else {
                                    const index = pageBreaks.indexOf('subsections');
                                    if (index !== -1) {
                                      pageBreaks.splice(index, 1);
                                    }
                                  }
                                  updateFormatSettings('pageBreaks', pageBreaks);
                                }}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">New subsections</span>
                            </label>
                            
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={bodyFormData.format.pageBreaks.includes('questions')}
                                onChange={(e) => {
                                  const pageBreaks = [...bodyFormData.format.pageBreaks];
                                  if (e.target.checked) {
                                    if (!pageBreaks.includes('questions')) {
                                      pageBreaks.push('questions');
                                    }
                                  } else {
                                    const index = pageBreaks.indexOf('questions');
                                    if (index !== -1) {
                                      pageBreaks.splice(index, 1);
                                    }
                                  }
                                  updateFormatSettings('pageBreaks', pageBreaks);
                                }}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">Avoid splitting questions across pages</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Live Preview */}
          <div className="w-1/2 bg-white border-l border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800">Live Preview</h3>
              <p className="text-sm text-gray-600">
                {templateType === 'cover' ? 'Cover page preview' : 'Body page preview (A4 format)'}
              </p>
            </div>
            <div className="h-full overflow-auto p-4">
              {htmlContent ? (
                <div
                  className="bg-white shadow-lg mx-auto border"
                  style={{
                    width: '210mm',
                    minHeight: '297mm',
                    transform: 'scale(0.4)',
                    transformOrigin: 'top left',
                    marginBottom: '-60%'
                  }}
                >
                  <iframe
                    srcDoc={combineHTMLCSS()}
                    className="w-full h-full border-none"
                    style={{ height: '297mm' }}
                    title="Template Preview"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <SafeIcon icon={templateType === 'cover' ? FiFileText : FiBook} className="text-4xl mb-2" />
                    <p>Fill the form and generate template to see preview</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CLO Selector Modal */}
      <AnimatePresence>
        {showCLOSelector && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg w-full max-w-4xl max-h-[80vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-800">Select CLO from Database</h3>
                  <button
                    onClick={() => setShowCLOSelector(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiX} />
                  </button>
                </div>
              </div>

              <div className="p-6">
                {availableCLOs.length === 0 ? (
                  <div className="text-center py-8">
                    <SafeIcon icon={FiTarget} className="mx-auto text-4xl text-gray-300 mb-4" />
                    <p className="text-gray-500">No CLOs available in database</p>
                    <p className="text-sm text-gray-400 mt-1">Create CLOs first to use this feature</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {availableCLOs.map((clo) => (
                      <div
                        key={clo.id}
                        onClick={() => selectCLOFromDatabase(clo)}
                        className="p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                                {clo.cloNumber}
                              </span>
                              <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-xs">
                                {clo.courseCode}
                              </span>
                            </div>
                            <p className="text-gray-800 font-medium">{clo.description}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Created {new Date(clo.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                            Select
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Full Preview Modal */}
      <AnimatePresence>
        {showPreview && htmlContent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden"
            >
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold">Full Template Preview</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={downloadPDF}
                    className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiPrinter} />
                    <span>Print/PDF</span>
                  </button>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="p-8 overflow-auto max-h-[calc(90vh-120px)] bg-gray-100">
                <div
                  className="bg-white shadow-2xl mx-auto"
                  style={{ width: '210mm', minHeight: '297mm' }}
                >
                  <iframe
                    srcDoc={combineHTMLCSS()}
                    className="w-full border-none"
                    style={{ height: '297mm' }}
                    title="Full Template Preview"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HTMLCSSEditor;